import { useMemo } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ScrollElement from '../../../global/Elements/ScrollElement';
import ManageGraph from './components/table/ManageGraph';
import SummaryGDAGraphs from '@/components/Graphs/SummaryGDA/SummaryGDAGraphs';
import SummaryVDAGraphs from '@/components/Graphs/SummaryVDA/SummaryVDAGraphs';
import MappingResults from '@/components/Mapping/MappingResults';
import { MappingDrillDownProvider } from '@/context/MappingDrillDownContext';
import DrillDownTableContainer from '@/components/Mapping/DrillDownTableContainer';
import DDAGraphs from '@/components/Graphs/DDA/DDAGraphs';
import ScoreModal from './components/modal/ScoreModal';
import { AdjustmentsHorizontalIcon, ChevronDoubleLeftIcon } from '@heroicons/react/16/solid';
import { mapItemsToSearchItems } from '../../../components/Table/types/index';
import { RightAsideSidebar } from './components/table/components/RightAsideSidebar';
import { MappingSidebarColumnProvider } from '@/context/MappingSidebarColumnContext';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import SharedButton from '../../../global/Button/Button';
import './SearchResults.css';

import { useSearchResultsState } from './hooks/useSearchResultsState';
import { LeftPanels } from './components/LeftPanels';
import { FullRecordDetailView } from './components/FullRecordDetailView';
import { BottomDetailPanel } from './components/BottomDetailPanel';
import { TabGridContent } from './components/TabGridContent';
import { SearchItemsStrip } from './components/SearchItemsStrip';
import { SearchResultsProps } from './utils/searchResultsHelpers';
import { buildSummaryMatchScopeKey, SummaryMatchProvider } from './context/SummaryMatchContext';

const graphMap: Record<string, any> = {
  GDA: SummaryGDAGraphs,
  VDA: SummaryVDAGraphs,
  DDA: DDAGraphs,
};

function SearchResultsInner({ dispatch }: SearchResultsProps) {
  const {
    // Context
    view,
    source,
    tab,
    searchItems,
    setSearchItems,
    mappingContent,
    restrictChemical,
    isAuthenticated,
    // Auth
    isTrial,
    isAcademic,
    // Sidebar layout
    isSidebarOpen,
    sidebarWidth,
    setSidebarOpen,
    isDesktop,
    // State
    tabIndex,
    selectedView,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
    scoreModalOpen,
    setScoreModalOpen,
    refreshScoresDropdown,
    setRefreshScoresDropdown,
    leftInfoPanelItem,
    leftSentencePanel,
    bottomDetailTab,
    setBottomDetailTab,
    bottomDetailRow,
    bottomDetailHeight,
    setBottomDetailHeight,
    sentenceCopied,
    sourceWeights,
    graphsColumnModel,
    setGraphsColumnModel,
    summaryColumnModel,
    setSummaryColumnModel,
    summaryColumnOrder,
    setSummaryColumnOrder,
    evidenceColumnModel,
    setEvidenceColumnModel,
    evidenceColumnOrder,
    setEvidenceColumnOrder,
    activeColumnPanel,
    setActiveColumnPanel,
    isMappingUrlHydrated,
    // Refs
    sentenceToCopyRef,
    summaryResetRef,
    evidenceResetRef,
    // Computed
    visibleTabs,
    searchItemsEntityPlural,
    groupedBottomDetails,
    relationSummary,
    columnOrder,
    searchTableSummaryEvidenceToggle,
    isFullRecordMode,
    showSummary,
    showEvidence,
    showManageGraph,
    useMappingSidebarState,
    useDrillDownMapping,
    currentMappingLevel,
    mappingSidebar,
    // Breakdown rows
    scoreBreakdownRow,
    setScoreBreakdownRow,
    prevalenceBreakdownRow,
    setPrevalenceBreakdownRow,
    evidenceTimelineRow,
    setEvidenceTimelineRow,
    // Handlers
    handleTabSelect,
    handleRemoveSearchItem,
    handleClearAllSearchItems,
    handleOpenEntityInfo,
    handleCopySentenceFromPanel,
    handleApplyWeights,
    handleApplySavedScore,
    handleDeleteSavedScore,
    handleClearWeights,
    handleApplySavedColumns,
    handleDeleteSavedColumn,
    handleResetToDefault,
    handleDownloadRowDetail,
    startBottomResize,
    closeBottomDetail,
    ensureTablesViewForDrilldown,
    closeSentencePanel,
    searchParams,
    setSearchParams,
    defaultFilterValue,
  } = useSearchResultsState(dispatch);

  const hasLeftPanel =
    leftInfoPanelItem ||
    leftSentencePanel ||
    scoreBreakdownRow ||
    prevalenceBreakdownRow ||
    evidenceTimelineRow;

  const sharedGridProps = {
    source,
    view,
    tab,
    searchItems,
    setSearchItems,
    restrictChemical,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
    defaultFilterValue,
    dispatch,
    sourceWeights,
    onScoreSaved: () => setRefreshScoresDropdown((prev: number) => prev + 1),
    onOpenSearchItemInfo: handleOpenEntityInfo,
    summaryEvidenceToggle: searchTableSummaryEvidenceToggle,
    summaryColumnModel,
    setSummaryColumnModel,
    summaryColumnOrder,
    setSummaryColumnOrder,
    summaryResetRef,
    evidenceColumnModel,
    setEvidenceColumnModel,
    evidenceColumnOrder,
    setEvidenceColumnOrder,
    evidenceResetRef,
  };

  const sharedSidebarProps = {
    isOpen: isSidebarOpen,
    onClose: () => setSidebarOpen(false),
    source,
    view,
    tab,
    searchItems: mapItemsToSearchItems(searchItems),
    onOpenSearchItemInfo: handleOpenEntityInfo,
    onRemoveSearchItem: handleRemoveSearchItem,
    onClearAllSearchItems: handleClearAllSearchItems,
    showMyScoreSection: !isAcademic && ['GDA', 'VDA'].includes(tab as string) && isAuthenticated,
    onOpenScoreModal: () => setScoreModalOpen(true),
    onApplySavedColumns: handleApplySavedColumns,
    onDeleteSavedColumns: handleDeleteSavedColumn,
    onApplySavedScore: handleApplySavedScore,
    onDeleteSavedScore: handleDeleteSavedScore,
    onClearWeights: handleClearWeights,
    hasActiveWeights: sourceWeights.length > 0,
    refreshScoresDropdown,
    sourceWeights,
    onResetToDefault: handleResetToDefault,
    isAcademic,
  };

  const summaryMatchScopeKey = useMemo(
    () => buildSummaryMatchScopeKey({ tab, view, source, searchItems }),
    [tab, view, source, searchItems],
  );

  return (
    <SummaryMatchProvider scopeKey={summaryMatchScopeKey}>
      <>
        <ScoreModal
          isOpen={scoreModalOpen}
          onClose={() => setScoreModalOpen(false)}
          onApplyWeights={handleApplyWeights}
          onScoreSaved={() => setRefreshScoresDropdown((prev: number) => prev + 1)}
          refreshTrigger={refreshScoresDropdown}
        />

        {isFullRecordMode && (
          <FullRecordDetailView
            bottomDetailRow={bottomDetailRow}
            groupedBottomDetails={groupedBottomDetails}
            relationSummary={relationSummary}
            source={source}
            tab={tab}
            isSidebarOpen={isSidebarOpen}
            sidebarWidth={sidebarWidth}
            isDesktop={isDesktop}
            hasLeftPanel={Boolean(hasLeftPanel)}
            searchParams={searchParams as any}
            setSearchParams={setSearchParams}
            handleDownloadRowDetail={handleDownloadRowDetail}
            bottomDetailTab={bottomDetailTab}
            setBottomDetailTab={setBottomDetailTab}
            ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
            isAcademic={isAcademic}
            isAuthenticated={isAuthenticated}
          />
        )}

        <LeftPanels
          leftInfoPanelItem={leftInfoPanelItem}
          leftSentencePanel={leftSentencePanel}
          scoreBreakdownRow={scoreBreakdownRow}
          prevalenceBreakdownRow={prevalenceBreakdownRow}
          evidenceTimelineRow={evidenceTimelineRow}
          sentenceToCopyRef={sentenceToCopyRef}
          sentenceCopied={sentenceCopied}
          isDesktop={isDesktop}
          view={view}
          onCloseInfoPanel={() => handleOpenEntityInfo(null)}
          onCloseSentencePanel={closeSentencePanel}
          onCloseScoreBreakdown={() => setScoreBreakdownRow(null)}
          onClosePrevalenceBreakdown={() => setPrevalenceBreakdownRow(null)}
          onCloseEvidenceTimeline={() => setEvidenceTimelineRow(null)}
          onCopySentence={handleCopySentenceFromPanel}
        />

        {selectedView === 'graphs' && (
          <RightAsideSidebar
            {...sharedSidebarProps}
            isEvidence={false}
            columnVisibilityModel={graphsColumnModel}
            setColumnVisibilityModel={updater => setGraphsColumnModel(updater)}
            isMapping={false}
          />
        )}

        {selectedView === 'tables' && (
          <RightAsideSidebar
            {...sharedSidebarProps}
            isEvidence={
              useMappingSidebarState
                ? mappingSidebar.state!.isEvidence
                : showEvidence && (!showSummary || activeColumnPanel === 'evidence')
            }
            columnVisibilityModel={
              useMappingSidebarState
                ? mappingSidebar.state!.columnVisibilityModel
                : !showEvidence || (showSummary && activeColumnPanel === 'summary')
                  ? (summaryColumnModel ?? {})
                  : (evidenceColumnModel ?? {})
            }
            setColumnVisibilityModel={
              useMappingSidebarState
                ? mappingSidebar.state!.setColumnVisibilityModel
                : updater => {
                    const useSummaryModel =
                      !showEvidence || (showSummary && activeColumnPanel === 'summary');
                    if (useSummaryModel) {
                      setSummaryColumnModel((prev: any) =>
                        typeof updater === 'function' ? updater(prev ?? {}) : updater,
                      );
                    } else {
                      setEvidenceColumnModel((prev: any) =>
                        typeof updater === 'function' ? updater(prev ?? {}) : updater,
                      );
                    }
                  }
            }
            columnPanelToggle={
              useMappingSidebarState
                ? mappingSidebar.state!.columnPanelToggle
                : showSummary && showEvidence
                  ? { active: activeColumnPanel, onChange: setActiveColumnPanel }
                  : undefined
            }
            isMapping={useMappingSidebarState}
            mappingContent={
              useMappingSidebarState && currentMappingLevel?.mappingContent
                ? (currentMappingLevel.mappingContent as any)
                : mappingContent
            }
            columnOrder={columnOrder}
            setColumnOrder={
              useMappingSidebarState
                ? mappingSidebar.state!.setColumnOrder
                : (order: string[]) => {
                    const useSummaryModel =
                      !showEvidence || (showSummary && activeColumnPanel === 'summary');
                    if (useSummaryModel) setSummaryColumnOrder(order);
                    else setEvidenceColumnOrder(order);
                  }
            }
          />
        )}

        <div
          className={`search-results-container ${selectedView === 'tables' ? 'flex' : ''}${isFullRecordMode ? ' hidden' : ''}`}
          data-testid="search-results"
          style={{
            marginLeft: hasLeftPanel ? (isDesktop ? 340 : 0) : 0,
            marginRight: isSidebarOpen ? (isDesktop ? sidebarWidth : 0) : 0,
            transition: 'margin-right 0.3s ease-out, margin-left 0.3s ease-out',
            paddingRight: isSidebarOpen ? '1%' : '3%',
            paddingLeft: hasLeftPanel ? '1%' : '3%',
          }}
        >
          <div
            className="pr-1 pl-1 md:p-5 md:pr-5 responsive-search-results"
            style={{ flex: '1', height: '100%', minWidth: 0 }}
          >
            <Tabs
              className="react-tabs superposed-content"
              selectedIndex={tabIndex}
              onSelect={index => handleTabSelect(index)}
            >
              <div className="responsive-search-tabs flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2 lg:flex-nowrap">
                <div className="d-flex min-w-0 flex-1 gap-2 md:justify-start">
                  <TabList className="react-tabs__tab-list col-auto">
                    {visibleTabs.map((t: any) => (
                      <Tab key={t.key} className="react-tabs__tab bg-white mb-0 my-2 my-lg-0 m-1">
                        {t.label}
                      </Tab>
                    ))}
                  </TabList>
                </div>

                {!isSidebarOpen && !isDesktop && (
                  <div className="md:ml-auto flex shrink-0 items-center mb-2">
                    <BaseTooltip content="Open table controls" position="bottom">
                      <SharedButton
                        type="button"
                        variant="terciary"
                        onClick={() => setSidebarOpen(!isSidebarOpen)}
                        aria-expanded={isSidebarOpen}
                        aria-label={isSidebarOpen ? 'Hide table controls' : 'Open table controls'}
                        data-testid="grid-controls-sidebar-toggle"
                        size="xs"
                        iconLeft={isDesktop ? <ChevronDoubleLeftIcon className="w-5" /> : null}
                        iconMargin={null}
                        className={`p-0 mb-0 ${!isDesktop ? 'pr-3' : ''}`}
                      >
                        {!isDesktop && (
                          <>
                            <AdjustmentsHorizontalIcon className="w-5 mx-2" />
                            {isSidebarOpen ? 'Hide controls' : 'Controls'}
                          </>
                        )}
                      </SharedButton>
                    </BaseTooltip>
                  </div>
                )}
              </div>

              <SearchItemsStrip
                searchItems={searchItems}
                entityPlural={searchItemsEntityPlural}
                onOpenItemInfo={handleOpenEntityInfo}
                onRemoveItem={handleRemoveSearchItem}
                onClearAll={handleClearAllSearchItems}
              />

              {sourceWeights.length > 0 &&
                selectedView === 'tables' &&
                ['GDA', 'VDA'].includes(tab as string) &&
                !isAcademic &&
                isAuthenticated && (
                  <div className="mb-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg
                        className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-blue-900">Custom Score Configuration Active</p>
                        <p className="text-xs text-blue-700 mt-1 mb-0">
                          You have a custom My Score configuration applied to this data.{' '}
                          {isAuthenticated
                            ? 'Use the "My Score" section in the Controls sidebar to manage or clear your configuration.'
                            : 'Use the "My Score" section in the Controls sidebar to modify or clear your configuration.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              <div className="mt-2">
                {visibleTabs.map((t: any) => (
                  <TabPanel key={t.key} className="tab-pane">
                    {useDrillDownMapping && selectedView === 'tables' ? (
                      <MappingDrillDownProvider useDrillDown={true}>
                        <DrillDownTableContainer
                          baseLevelId={`base-${t.key}`}
                          baseLabel="Results"
                          dispatch={dispatch}
                          panelTab={t.key}
                          onOpenEntityInfo={handleOpenEntityInfo}
                          isMappingUrlHydrated={isMappingUrlHydrated}
                        >
                          <ScrollElement name="SearchTable">
                            <TabGridContent
                              {...sharedGridProps}
                              panelTab={t.key}
                              selectedView={selectedView}
                              showSummary={showSummary}
                              showEvidence={showEvidence}
                              activeColumnPanel={activeColumnPanel}
                            />
                          </ScrollElement>
                        </DrillDownTableContainer>
                      </MappingDrillDownProvider>
                    ) : (
                      <>
                        <ScrollElement name="SearchTable">
                          <TabGridContent
                            {...sharedGridProps}
                            panelTab={t.key}
                            selectedView={selectedView}
                            showSummary={showSummary}
                            showEvidence={showEvidence}
                            activeColumnPanel={activeColumnPanel}
                          />
                        </ScrollElement>
                        <ScrollElement name="Summary">
                          {selectedView === 'graphs' && showManageGraph && (
                            <ManageGraph
                              source={source}
                              view={view}
                              tab={tab}
                              searchItems={searchItems}
                              setSearchItems={setSearchItems}
                              sectionTitle={t.label}
                              names={searchItems}
                              title={view}
                              Graph={graphMap[t.key]}
                            />
                          )}
                        </ScrollElement>
                        <ScrollElement name="Mapping">
                          {selectedView === 'tables' && mappingContent?.isActive && (
                            <MappingResults mappingContent={mappingContent} dispatch={dispatch} />
                          )}
                        </ScrollElement>
                      </>
                    )}
                  </TabPanel>
                ))}
              </div>
            </Tabs>
          </div>
        </div>

        {bottomDetailRow && !isFullRecordMode && (
          <BottomDetailPanel
            bottomDetailRow={bottomDetailRow}
            bottomDetailHeight={bottomDetailHeight}
            groupedBottomDetails={groupedBottomDetails}
            relationSummary={relationSummary}
            source={source}
            tab={tab}
            isSidebarOpen={isSidebarOpen}
            sidebarWidth={sidebarWidth}
            isDesktop={isDesktop}
            hasLeftPanel={Boolean(hasLeftPanel)}
            searchParams={searchParams as any}
            setSearchParams={setSearchParams}
            onClose={closeBottomDetail}
            startBottomResize={startBottomResize}
            bottomDetailTab={bottomDetailTab}
            setBottomDetailTab={setBottomDetailTab}
            setBottomDetailHeight={setBottomDetailHeight}
            ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
            isAcademic={isAcademic}
            isAuthenticated={isAuthenticated}
          />
        )}
      </>
    </SummaryMatchProvider>
  );
}

function SearchResults(props: SearchResultsProps) {
  return (
    <MappingSidebarColumnProvider>
      <SearchResultsInner {...props} />
    </MappingSidebarColumnProvider>
  );
}

export default SearchResults;
